import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';

import css from './MultiSelectCombobox.module.css';

/**
 * FASE2-WIZARD-2b — Combobox de selección múltiple con buscador.
 *
 * Núcleo extraído de SearchPage/MultiSelectWithSearch, que pasa a ser un
 * envoltorio suyo. El motivo es de producto: quien elige provincias al PUBLICAR
 * debe usar el mismo control que al BUSCAR. Antes esa interacción vivía atada al
 * contrato de filtro (queryParamNames / initialValues / onSubmit sobre la URL) y
 * no se podía reutilizar en un formulario.
 *
 * Este componente es CONTROLADO: recibe `value` (array) y emite `onChange`. No
 * sabe nada de URLs ni de react-final-form; las reglas de negocio —exclusividad,
 * topes, obligatoriedad— las pone quien lo usa, vía `isOptionDisabled` y `notice`.
 *
 * @component
 * @param {Object} props
 * @param {Array<{value: string, label: string}>} props.options opciones disponibles
 * @param {Array<string>} props.value valores seleccionados
 * @param {Function} props.onChange recibe el nuevo array de valores
 * @param {string} props.placeholder texto del disparador cuando no hay selección
 * @param {string} props.searchPlaceholder texto del buscador interno
 * @param {string} props.emptyLabel texto cuando la búsqueda no encuentra nada
 * @param {string} props.clearLabel texto del botón de limpiar selección
 * @param {string} [props.doneLabel] texto del botón de cerrar (por defecto "Hecho")
 * @param {Function} [props.isOptionDisabled] (option) => bool; deshabilita opciones
 * @param {React.ReactNode} [props.notice] aviso dentro del panel (p. ej. tope alcanzado)
 * @param {React.ReactNode} [props.helpText] texto de ayuda bajo el control
 * @param {boolean} [props.invalid] pinta el disparador en estado de error
 * @param {Function} [props.onBlur] se llama al cerrar el panel (para `touched`)
 */
const ChevronIcon = ({ open }) => (
  <svg
    className={classNames(css.chevron, { [css.chevronOpen]: open })}
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M6 9l6 6 6-6"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CheckIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M20 6L9 17l-5-5"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Normalización insensible a acentos y mayúsculas, para que "malaga" encuentre
// "Málaga". Es la misma que usaba el filtro.
const normalize = str =>
  String(str || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();

const MultiSelectCombobox = props => {
  const {
    className,
    options = [],
    value = [],
    onChange,
    placeholder,
    searchPlaceholder,
    emptyLabel,
    clearLabel,
    // "Hecho", no "Guardar" ni "Aplicar": lo elegido ya está guardado al
    // marcar cada opción, y esas palabras sugerirían que queda algo por
    // confirmar. Este botón solo cierra.
    doneLabel = 'Hecho',
    isOptionDisabled,
    notice,
    helpText,
    invalid = false,
    onBlur,
  } = props;

  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const wrapperRef = useRef(null);

  const close = useCallback(() => {
    setIsOpen(false);
    setSearchQuery('');
    if (onBlur) {
      onBlur();
    }
  }, [onBlur]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleDocClick = e => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) close();
    };
    const handleKey = e => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('mousedown', handleDocClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleDocClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [isOpen, close]);

  const filteredOptions = useMemo(() => {
    const q = searchQuery.trim();
    if (q === '') return options;
    const nq = normalize(q);
    return options.filter(opt => normalize(opt.label).includes(nq));
  }, [searchQuery, options]);

  const toggleOption = option => {
    if (isOptionDisabled && isOptionDisabled(option)) {
      return;
    }
    const next = value.includes(option.value)
      ? value.filter(v => v !== option.value)
      : [...value, option.value];
    onChange(next);
  };

  const triggerLabel =
    value.length === 0
      ? placeholder
      : value.map(v => options.find(o => o.value === v)?.label || v).join(', ');
  const isPlaceholder = value.length === 0;

  return (
    <div className={classNames(css.wrapper, className)} ref={wrapperRef}>
      <button
        type="button"
        className={classNames(css.trigger, {
          [css.triggerOpen]: isOpen,
          [css.triggerPlaceholder]: isPlaceholder,
          [css.triggerInvalid]: invalid,
        })}
        onClick={() => (isOpen ? close() : setIsOpen(true))}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span className={css.triggerText}>{triggerLabel}</span>
        <ChevronIcon open={isOpen} />
      </button>

      {isOpen ? (
        <div className={css.panel} role="listbox" aria-multiselectable="true">
          <div className={css.searchZone}>
            <input
              type="text"
              className={css.searchInput}
              placeholder={searchPlaceholder}
              aria-label={searchPlaceholder}
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              autoFocus
            />
          </div>

          {notice ? <p className={css.notice}>{notice}</p> : null}

          <ul className={css.list}>
            {filteredOptions.length === 0 ? (
              <li className={css.emptyState}>{emptyLabel}</li>
            ) : (
              filteredOptions.map(opt => {
                const checked = value.includes(opt.value);
                const disabled = !!(isOptionDisabled && isOptionDisabled(opt));
                return (
                  <li
                    key={opt.value}
                    className={classNames(css.option, {
                      [css.optionChecked]: checked,
                      [css.optionDisabled]: disabled,
                    })}
                    role="option"
                    aria-selected={checked}
                    aria-disabled={disabled}
                    tabIndex={disabled ? -1 : 0}
                    onClick={() => toggleOption(opt)}
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        toggleOption(opt);
                      }
                    }}
                  >
                    <span className={css.checkbox}>{checked ? <CheckIcon /> : null}</span>
                    <span className={css.optionLabel}>{opt.label}</span>
                  </li>
                );
              })
            )}
          </ul>

          {/* FASE2-WIZARD-2b-ter: el pie va SIEMPRE, no solo cuando hay algo
              elegido. En móvil el panel ocupa casi toda la pantalla y no queda
              "fuera" al que tocar para cerrarlo, así que sin este botón el
              usuario se queda atrapado: no hay Escape ni clic fuera. Que
              aparezca solo al seleccionar sería peor todavía —surgiría de
              golpe, desplazando el contenido— y dejaría sin salida justo a
              quien no ha elegido nada. Lo que cambia con la selección es el
              énfasis, no la presencia.

              El pie es `flex: 0 0 auto` dentro de un panel en columna cuya
              lista lleva el `overflow`, así que queda fijo al fondo sin
              necesidad de posicionarlo. */}
          <div className={css.footer}>
            {value.length > 0 ? (
              <button type="button" className={css.clearAll} onClick={() => onChange([])}>
                {clearLabel}
              </button>
            ) : null}
            <button
              type="button"
              className={classNames(css.doneButton, {
                [css.doneButtonActive]: value.length > 0,
              })}
              onClick={close}
            >
              {doneLabel}
            </button>
          </div>
        </div>
      ) : null}

      {helpText ? <p className={css.helpText}>{helpText}</p> : null}
    </div>
  );
};

export default MultiSelectCombobox;
